import React from 'react';
import './styles.scss';
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useTranslation } from 'react-i18next';
import Rating from './Rating';
import { FaStar, FaRegStar, FaShareAlt, FaPlayCircle } from 'react-icons/fa';
import Toast from 'react-bootstrap/Toast';
import { getAttributor } from '../../utils/activityTracking';
import { shareOffer, buildShareLink } from '../../utils/shareUtils';
import { useClickoutTracking } from '../../hooks/useClickoutTracking';
import { trackEvent, setUserProperties } from '../../utils/analytics';
import VideoModal from '../VideoModal';
import BookingModal from '../BookingModal';
import { acrissToCategory, acrissToCarType } from './itemHelpers';
import { isSameOffer } from '../../utils/offerIdMatch';

// Helper component: Car specs section
const CarSpecsSection = ({ item, t, locationSlug, isAirport }) => (
  <>
    {item.spec.seats ? (
      <Row key='seats'>
        <Col xs={5} md={3}>
          <b>{t('seats')}</b>
        </Col>
        <Col xs={7} md={9}>
          {t(item.spec.seats)}
        </Col>
      </Row>
    ) : ''}
    {(item.spec.maxLuggageCapacity && item.spec.maxLuggageCapacity !== '?') || item.spec.luggage || (item.spec.luggageAgg?.median) ? (() => {
      let luggageDisplay = null;
      if (item.spec.maxLuggageCapacity && item.spec.maxLuggageCapacity !== '?') {
        const liters = parseInt(item.spec.maxLuggageCapacity);
        let luggageEstimate = { large: 0, medium: 0, cabin: 0 };
        try {
          luggageEstimate = require('../../utils/luggageEstimate').estimateLuggageFromLiters(liters);
        } catch {}
        luggageDisplay = (
          <span className="luggage-capacity-estimate" data-liters={liters}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24" style={{ marginBlockEnd: '4px', marginInlineEnd: '4px', verticalAlign: 'middle' }}>
              <path d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2h5c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h5zm2-2v2h4V4h-4zm-7 6v8h18V10H3zm2 2h2v4H5v-4zm4 0h2v4H9v-4zm4 0h2v4h-2v-4zm4 0h2v4h-2v-4z"/>
              <circle cx="19" cy="16" r="1"/>
              <circle cx="5" cy="16" r="1"/>
            </svg>
            {luggageEstimate.large || 0}
          </span>
        );
      } else if (item.spec.luggage) {
        luggageDisplay = (
          <span className="luggage-capacity-estimate" data-liters={item.spec.luggage * 220}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24" style={{ marginBlockEnd: '4px', marginInlineEnd: '4px', verticalAlign: 'middle' }}>
              <path d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2h5c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h5zm2-2v2h4V4h-4zm-7 6v8h18V10H3zm2 2h2v4H5v-4zm4 0h2v4H9v-4zm4 0h2v4h-2v-4zm4 0h2v4h-2v-4z"/>
              <circle cx="19" cy="16" r="1"/>
              <circle cx="5" cy="16" r="1"/>
            </svg>
            {item.spec.luggage}
          </span>
        );
      } else if (item.spec.luggageAgg?.median) {
        const liters = parseInt(item.spec.luggageAgg.median);
        let luggageEstimate = { large: 0, medium: 0, cabin: 0 };
        try {
          luggageEstimate = require('../../utils/luggageEstimate').estimateLuggageFromLiters(liters);
        } catch {}
        luggageDisplay = (
          <span className="luggage-capacity-estimate-aggregate" data-liters={liters}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-exclamation-triangle" viewBox="0 0 24 24" style={{ marginBlockEnd: '4px', marginInlineEnd: '4px', verticalAlign: 'middle' }}>
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            {luggageEstimate.large || 0}
          </span>
        );
      }
      return (
        <Row key='luggage'>
          <Col xs={5} md={3}>
            <b>{t('luggageCapacityTooltipTitle')}</b>
          </Col>
          <Col xs={7} md={9}>
            {luggageDisplay}
          </Col>
        </Row>
      );
    })() : ''}
    <Row key='kmLimit'>
      <Col xs={5} md={3}>
        <b>{t('kmLimit')}</b>
      </Col>
      <Col xs={7} md={9}>
        {t(item.terms.kmLimit)}
      </Col>
    </Row>
    <Row key='cancellationTerms'>
      <Col xs={5} md={3}>
        <b>{t('cancellationTerms')}</b>
      </Col>
      <Col xs={7} md={9}>
        {t(item.terms.cancellationTerms)}
      </Col>
    </Row>
    <Row key='carGroup'>
      <Col xs={5} md={3}>
        <b>{t('carGroup')}</b> <br />
      </Col>
      <Col xs={7} md={9}>
        <div key='orSimilarInGroup'>
          {item.isProperGroup ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check-circle text-success goodAcriss" viewBox="0 0 16 16" data-group={item.group} data-model={item.model} data-category={acrissToCategory(item.group)}>
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
            </svg>
          ) : ""}
          {item.isGoodGroup && !item.isProperGroup ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check-circle text-success infoAcriss" viewBox="0 0 16 16" data-group={item.group} data-model={item.model} data-category={acrissToCategory(item.group)} data-correct-group={item.correctGroup}>
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
            </svg>
          ) : ""}
          {!item.isGoodGroup && !item.isProperGroup && item.correctGroup ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-exclamation-circle text-danger badAcriss" viewBox="0 0 16 16" data-group={item.group} data-model={item.model} data-category={acrissToCategory(item.group)} data-correct-group={item.correctGroup}>
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
            </svg>
          ) : ""}
          {" " + t(acrissToCarType(item.group))} {t(acrissToCategory(item.group))} {t(item.spec.transmission)} ({item.group})
        </div>
      </Col>
    </Row>
    <Row key='localSupplier'>
      <Col xs={5} md={3}>
        <b>{t('localSupplier')}</b>
      </Col>
      <Col xs={7} md={9}>
        {item.localSupplier.logoUrl ? <img src={item.localSupplier.logoUrl} className="supplier-logo" alt={item.localSupplier.displayName} title={item.localSupplier.displayName} /> : item.localSupplier.displayName}
      </Col>
    </Row>
    {!item?.localSupplier?.googlePlace || typeof item?.localSupplier?.googlePlace === "undefined" ? "" : (
      <Row key='branchRating'>
        <Col xs={5} md={3}>
          <b>{t('branchRating')}</b>
        </Col>
        <Col xs={7} md={9}>
          <Rating localSupplier={item.localSupplier} locationSlug={locationSlug} />
        </Col>
      </Row>
    )}
    {isAirport ? (
      <Row key='deskLocation'>
        <Col xs={5} md={3}>
          <b>{t('deskLocation')}</b>
        </Col>
        <Col xs={7} md={9}>
          {item.localSupplier.deskLocation ? t(item.localSupplier.deskLocation) + ' ' : '- '}
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-question-circle deskLocation" data-desk-location={item?.localSupplier?.deskLocation} data-location-slug={locationSlug} viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
          </svg>
        </Col>
      </Row>
    ) : ""}
    {item.includes ? (
      <Row key='included'>
        <Col xs={5} md={3}>
          <b>{t('included')}</b>
        </Col>
        <Col xs={7} md={9}>
          {item.includes}
        </Col>
      </Row>
    ) : ''}
  </>
);

// Helper component: All badge rows
const BadgesSection = ({ item, t, isWinterSearch }) => (
  <>
    {item.terms.zeroExcess ? (
      <Row key='zeroExcess'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-info zeroExcess'>{t('zeroExcess')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.tireAndGlassInsuranceIncluded ? (
      <Row key='tireAndGlassInsuranceIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-info tireAndGlassInsuranceIncluded'>{t('tireAndGlassInsuranceIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.premiumCustomerService ? (
      <Row key='premiumCustomerService'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-danger'>{t('premiumCustomerService')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.additionalDriverIncluded ? (
      <Row key='additionalDriverIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-success'>{t('additionalDriverIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.zeroPrepayment ? (
      <Row key='zeroPrepayment'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-info'>{t('zeroPrepayment')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.excessRefundIncluded ? (
      <Row key='excessRefundIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-success excessRefundIncluded'>{t('excessRefundIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.mandatoryCoverageNotIncluded ? (
      <Row key='mandatoryCoverageNotIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-danger mandatoryCoverageNotIncluded'>{t('mandatoryCoverageNotIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.spec.oldModel ? (
      <Row key='oldModelWarning'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-warning oldModelWarning'>
            {t('oldModelWarning')}
          </span>
        </Col>
      </Row>
    ) : ''}
    {!item.terms.mandatoryCoverageNotIncluded && !item.terms.zeroExcess ? (
      <Row key='basicCoverageIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-info basicCoverageIncluded'>{t('basicCoverageIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.oneWayIncluded ? (
      <Row key='oneWayIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-info oneWayIncluded'>{t('oneWayIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {!item.terms.oneWayIncluded && item.puLocation !== item.doLocation ? (
      <Row key='checkOneWayFee'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-danger checkOneWayFee'>{t('checkOneWayFee')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.crossBorderIncluded ? (
      <Row key='crossBorderIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-success crossBorderIncluded'>{t('crossBorderIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.terms.winterEquipmentIncluded && isWinterSearch ? (
      <Row key='winterEquipmentIncluded'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-success winterEquipmentIncluded'>{t('winterEquipmentIncluded')}</span>
        </Col>
      </Row>
    ) : ''}
    {!item.spec.ac ? (
      <Row key='noAirCon'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-primary'>{t('noAirCon')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.spec.is4x4 ? (
      <Row key='4x4'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-dark'>{t('4x4')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.spec.isHybrid ? (
      <Row key='hybrid'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-success'>{t('hybrid')}</span>
        </Col>
      </Row>
    ) : ''}
    {item.spec.isElectric ? (
      <Row key='electric'>
        <Col xs={5} md={3}>&nbsp;</Col>
        <Col xs={7} md={9}>
          <span className='badge badge-success'>{t('electric')}</span>
        </Col>
      </Row>
    ) : ''}
  </>
);

// Helper component: Pricing section
const PricingSection = ({ item, t, locale }) => (
  <div className="note">
    {t('priceForEntirePeriod')}&nbsp;
    {item.payment && item.payment.vatNote ?
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className={`bi bi-question-circle p-8 vatNote`} data-note={item.payment.vatNote} viewBox="0 0 16 16">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
        <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
      </svg>
      : ''}
    <br />
    {t('payNowNote')} {item.payment && item.payment.renderPayNow && typeof item.payment.renderPayNow !== 'object'
      ? item.payment.renderPayNow.toLocaleString(locale, { style: 'currency', currency: item.payment.renderCurrency, maximumFractionDigits: 0, minimumFractionDigits: 0, })
      : 0
    }
  </div>
);

// Helper component: Action buttons (Quick Preview / View Deal)
const ActionButtons = ({ item, t, trackOutbound, trackQuickPreview, setShowBookingModal }) => (
  <>
    {item.directBooking ? (
      <Button className="mt-1 btn-block" size="lg" variant="success" onClick={() => {
        trackQuickPreview();
        setShowBookingModal(true);
      }}>{t('quickPreview')}</Button>
    ) : (
      <>
        <Button className="mt-1 btn-block" target="_blank" href={item.link || item.deeplink} size="lg" variant="success" data-supplier={item.key} onClick={trackOutbound} onAuxClick={e => e.preventDefault()} onContextMenu={e => e.preventDefault()} referrerPolicy="unsafe-url">{t('viewDeal')}</Button>
        <div className="note mt-1">
          {t('toSite')} {t(item.key)}&nbsp;
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-question-circle p-8 website" data-website={item?.key} viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
          </svg>
        </div>
      </>
    )}
  </>
);

const Item = ({
  dir,
  item = {},
  onItemClicked,
  cid,
  isAirport,
  locationSlug,
  savedOffers = [],
  toggleStarOffer,
  searchKey,
  params,
  isWinterSearch,
  onShowBookingModal,
  variant = 'desktop' // 'desktop' or 'mobile'
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [showToast, setShowToast] = React.useState(false);
  const [toastMsg, setToastMsg] = React.useState("");
  const [showVideoModal, setShowVideoModal] = React.useState(false);
  const [showBookingModal, setShowBookingModal] = React.useState(false);

  const { recordClickout } = useClickoutTracking({
    enableDebounce: true,
    debounceMs: 1000,
    enableLogging: true
  });

  const isStarred = savedOffers.some(id => isSameOffer(item, id));
  const isRtl = dir === 'rtl';
  const isMobile = variant === 'mobile';

  const trackOutbound = (e) => {
    const url = e.target.getAttribute("href");
    const supplier = e.target.getAttribute("data-supplier");
    if (searchKey) {
      // Prepare search data to send with clickout
      const searchData = params ? {
        params: params,
        savedOffers: savedOffers || [],
        filters: {} // todo consider sending filters data?
      } : null;
      
      recordClickout(searchKey, item, searchData);
    }
    trackEvent('Outbound Click', {
      'event_category': 'Conversion',
      'event_label': supplier,
    });
    setUserProperties({ 'custom_client_id': cid });
    console.log(url, supplier);
    if (onItemClicked) {
      onItemClicked(item);
    }
  };

  const trackQuickPreview = () => {
    trackEvent('Quick Preview', {
      'event_category': 'Conversion',
      'event_label': item.model || 'Car'
    });
    setUserProperties({ 'custom_client_id': cid });
  };

  // Shares the /go link built by buildShareLink (cid removed, utm_campaign from the current attributor).
  const shareUrl = buildShareLink(item.link, getAttributor());

  const handleShare = async (e) => {
    e.stopPropagation();
    if (!shareUrl) return;
    await shareOffer({
      url: shareUrl,
      title: t('shareOffer'),
      setToastMsg,
      setShowToast,
      shareLinkCopiedMsg: t('shareLinkCopied') || 'Share link copied!',
      shareLinkCopyFailedMsg: t('shareLinkCopyFailed') || 'Failed to copy link.'
    });
  };

  // Shared helpers
  const carImageSection = (
    <div className={isMobile ? "item-image-mobile" : "item-image"}>
      {item.image ? <img src={item.image} className={isMobile ? "w-100" : ''} alt={item.model} /> : null}
      {item.ytVideoId && (
        <button className={isMobile ? "video-overlay-btn-mobile" : "video-overlay-btn"} onClick={() => {
          trackEvent('Product Video View', {
            'event_category': 'Product Engagement',
            'event_label': item.model || 'Car Video'
          });
          setShowVideoModal(true);
        }} title={t('videoModalTitle')}>
          <FaPlayCircle size={isMobile ? 20 : 16} color="white" />
          <span>{t('playVideoReview')}</span>
        </button>
      )}
    </div>
  );

  const paymentSection = (
    <div className="payment">
      <h2 className="mb-0">{item.payment && item.payment.renderTotal && typeof item.payment.renderTotal !== 'object'
        ? item.payment.renderTotal.toLocaleString(locale, {style: 'currency', currency: item.payment.renderCurrency, maximumFractionDigits: 0, minimumFractionDigits: 0,})
        : 0
      }</h2>
      {item.payment.showVatIncludedNote && <div className="vat-included-note">{t('vatIncluded')}</div>}
      <PricingSection item={item} t={t} locale={locale} />
    </div>
  );

  const specsAndBadges = (
    <>
      <CarSpecsSection item={item} t={t} locationSlug={locationSlug} isAirport={isAirport} />
      <BadgesSection item={item} t={t} isWinterSearch={isWinterSearch} />
    </>
  );

  // Single unified layout using Bootstrap responsive grid
  return (
    <>
      <Row noGutters={isMobile} className={`item-wrapper ${!isMobile ? 'mx-0' : ''} mb-1 mb-md-2 rounded`}>
        <div className={`offer-action-bar-abs${isRtl ? ' rtl' : ''}`}>
          <button type="button" className="action-icon-btn" title={isStarred ? t('unstarOffer') : t('starOffer')} onClick={e => { e.stopPropagation(); toggleStarOffer(item); }}>
            {isStarred ? <FaStar color="#f5c518" size={20} /> : <FaRegStar color="#888" size={20} />}
          </button>
          {shareUrl && (
            <button type="button" className="action-icon-btn" title={t('shareOffer')} onClick={handleShare}>
              <FaShareAlt color="#888" size={20} />
            </button>
          )}
        </div>

        {/* Image and Model - Full width on mobile (xs={12}), md={4} on desktop */}
        <Col xs={12} md={4} className="">
          <h3 dir='ltr'>{item.model}</h3>
          {carImageSection}
        </Col>

        {/* Specs - Full width on mobile (xs={12}), md={8} on desktop */}
        <Col xs={12} md={8} className="spec">
          {specsAndBadges}
        </Col>

        {/* Payment section - only show in md={9} on desktop, render in submit col on mobile */}
        {!isMobile && (
          <Col md={9} className={`payment text-${isRtl ? "right" : "left"}`}>
          </Col>
        )}

        {/* Payment and Action Buttons - Full width on mobile, md={3} on desktop */}
        <Col xs={12} md={3} className={`submit text-${isMobile ? "center" : isRtl ? "right" : "left"} ${isMobile ? 'mt-3' : ''}`}>
          {paymentSection}
          <ActionButtons item={item} t={t} trackOutbound={trackOutbound} trackQuickPreview={trackQuickPreview} setShowBookingModal={setShowBookingModal} />
        </Col>
      </Row>

      {/* Toast notification - shared between mobile and desktop */}
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ pointerEvents: 'auto' }}>
          <Toast onClose={() => setShowToast(false)} show={showToast} delay={2500} autohide>
            <Toast.Body>{toastMsg}</Toast.Body>
          </Toast>
        </div>
      </div>

      {/* Modals - shared between mobile and desktop */}
      <VideoModal show={showVideoModal} onHide={() => setShowVideoModal(false)} videoId={item.ytVideoId} dir={dir} />
      <BookingModal show={showBookingModal} onHide={() => setShowBookingModal(false)} offer={item} isAirport={isAirport} dir={dir} cid={cid} />
    </>
  );
};

export default Item;
